// Geolocation and Currency Detection Utility
import axios from 'axios';

export class GeoLocationService {
  constructor() {
    this.userLocation = null;
    this.userCurrency = null;
    this.exchangeRates = null;
    this.defaultCurrency = 'INR';
    this.supportedCurrencies = ['INR', 'USD', 'EUR'];
  }

  // Get user's geolocation using IP-based service
  async getUserLocation() {
    try {
      // Try multiple services for better reliability
      const services = [
        'https://ipapi.co/json/',
        'https://ipgeolocation.abstractapi.com/v1/?api_key=free',
        'https://freegeoip.app/json/'
      ];

      for (const service of services) {
        try {
          const response = await axios.get(service, { timeout: 5000 });
          
          if (response.data && response.data.country_code) {
            this.userLocation = {
              country: response.data.country_name || response.data.country,
              countryCode: response.data.country_code,
              city: response.data.city,
              region: response.data.region || response.data.region_name,
              timezone: response.data.timezone,
              currency: response.data.currency || this.getCurrencyByCountry(response.data.country_code)
            };
            
            this.userCurrency = this.userLocation.currency;
            return this.userLocation;
          }
        } catch (serviceError) {
          console.warn(`Service ${service} failed:`, serviceError.message);
          continue;
        }
      }
      
      // Fallback to browser geolocation
      return await this.getBrowserLocation();
    } catch (error) {
      console.error('Error getting user location:', error);
      return this.getDefaultLocation();
    }
  }

  // Browser-based geolocation (requires user permission)
  async getBrowserLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(this.getDefaultLocation());
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Reverse geocoding using coordinates
            const response = await axios.get(
              `https://api.opencagedata.com/geocode/v1/json?q=${position.coords.latitude}+${position.coords.longitude}&key=free&limit=1`
            );
            
            const result = response.data.results[0];
            if (result) {
              this.userLocation = {
                country: result.components.country,
                countryCode: result.components.country_code?.toUpperCase(),
                city: result.components.city || result.components.town,
                region: result.components.state,
                timezone: result.annotations.timezone.name,
                currency: result.annotations.currency.iso_code
              };
              
              this.userCurrency = this.userLocation.currency;
              resolve(this.userLocation);
            } else {
              resolve(this.getDefaultLocation());
            }
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            resolve(this.getDefaultLocation());
          }
        },
        () => {
          resolve(this.getDefaultLocation());
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    });
  }

  // Get currency by country code
  getCurrencyByCountry(countryCode) {
    const currencyMap = {
      'US': 'USD', 'IN': 'INR', 'GB': 'GBP', 'DE': 'EUR', 'FR': 'EUR',
      'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 'CA': 'CAD', 'AU': 'AUD',
      'SG': 'SGD', 'JP': 'JPY', 'CN': 'CNY', 'BR': 'BRL', 'MX': 'MXN',
      'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN',
      'TR': 'TRY', 'ZA': 'ZAR', 'KR': 'KRW', 'TH': 'THB', 'MY': 'MYR',
      'PH': 'PHP', 'ID': 'IDR', 'VN': 'VND', 'BD': 'BDT', 'PK': 'PKR',
      'LK': 'LKR', 'NP': 'NPR', 'AE': 'AED', 'SA': 'SAR', 'EG': 'EGP'
    };
    
    return currencyMap[countryCode] || this.defaultCurrency;
  }

  // Default location (India)
  getDefaultLocation() {
    this.userLocation = {
      country: 'India',
      countryCode: 'IN',
      city: 'Mumbai',
      region: 'Maharashtra',
      timezone: 'Asia/Kolkata',
      currency: 'INR'
    };
    
    this.userCurrency = 'INR';
    return this.userLocation;
  }

  // Get exchange rates
  async getExchangeRates() {
    try {
      // Try multiple exchange rate services
      const services = [
        'https://api.exchangerate-api.com/v4/latest/INR',
        'https://api.fixer.io/latest?base=INR&access_key=fixer_api_key',
        'https://api.currencylayer.com/live?access_key=currency_layer_key&source=INR'
      ];

      for (const service of services) {
        try {
          const response = await axios.get(service, { timeout: 5000 });
          
          if (response.data && response.data.rates) {
            this.exchangeRates = {
              base: 'INR',
              rates: response.data.rates,
              timestamp: Date.now()
            };
            
            // Cache exchange rates for 1 hour
            localStorage.setItem('exchangeRates', JSON.stringify(this.exchangeRates));
            return this.exchangeRates;
          }
        } catch (serviceError) {
          console.warn(`Exchange rate service failed:`, serviceError.message);
          continue;
        }
      }
      
      // Use cached rates if available
      const cached = localStorage.getItem('exchangeRates');
      if (cached) {
        const cachedRates = JSON.parse(cached);
        // Check if cache is less than 1 hour old
        if (Date.now() - cachedRates.timestamp < 3600000) {
          this.exchangeRates = cachedRates;
          return this.exchangeRates;
        }
      }
      
      // Fallback exchange rates (approximate)
      return this.getFallbackRates();
    } catch (error) {
      console.error('Error getting exchange rates:', error);
      return this.getFallbackRates();
    }
  }

  // Fallback exchange rates (simplified for INR, USD, EUR only)
  getFallbackRates() {
    this.exchangeRates = {
      base: 'INR',
      rates: {
        'USD': 0.012, // 1 INR = 0.012 USD
        'EUR': 0.011, // 1 INR = 0.011 EUR
        'INR': 1 // Base currency
      },
      timestamp: Date.now()
    };
    
    return this.exchangeRates;
  }

  // Convert price from INR to target currency
  convertPrice(priceInINR, targetCurrency) {
    if (!this.exchangeRates) {
      console.warn('Exchange rates not loaded, using fallback');
      this.getFallbackRates();
    }

    if (targetCurrency === 'INR') {
      return {
        amount: priceInINR,
        currency: 'INR',
        symbol: '₹'
      };
    }

    const rate = this.exchangeRates.rates[targetCurrency];
    if (!rate) {
      console.warn(`Exchange rate not found for ${targetCurrency}, using INR`);
      return {
        amount: priceInINR,
        currency: 'INR',
        symbol: '₹'
      };
    }

    const convertedAmount = (priceInINR * rate).toFixed(2);
    
    return {
      amount: convertedAmount,
      currency: targetCurrency,
      symbol: this.getCurrencySymbol(targetCurrency)
    };
  }

  // Get currency symbol (simplified)
  getCurrencySymbol(currency) {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€'
    };
    
    return symbols[currency] || currency;
  }

  // Get recommended data center location based on user location
  getRecommendedDataCenter() {
    if (!this.userLocation) {
      return 'India'; // Default
    }

    const countryCode = this.userLocation.countryCode;
    
    // North America
    if (['US', 'CA', 'MX'].includes(countryCode)) {
      return 'USA';
    }
    
    // Europe
    if (['DE', 'FR', 'GB', 'IT', 'ES', 'NL', 'CH', 'SE', 'NO', 'DK', 'PL', 'TR'].includes(countryCode)) {
      return 'Germany';
    }
    
    // Asia-Pacific (excluding India)
    if (['SG', 'MY', 'TH', 'PH', 'ID', 'VN', 'AU', 'NZ', 'JP', 'KR', 'CN', 'HK'].includes(countryCode)) {
      return 'Singapore';
    }
    
    // India and neighboring countries
    return 'India';
  }

  // Format price with proper localization
  formatPrice(price, currency) {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericPrice);
    } catch (error) {
      // Fallback formatting
      const symbol = this.getCurrencySymbol(currency);
      return `${symbol}${numericPrice.toFixed(2)}`;
    }
  }

  // Initialize the service
  async initialize() {
    try {
      await Promise.all([
        this.getUserLocation(),
        this.getExchangeRates()
      ]);
      
      return {
        location: this.userLocation,
        currency: this.userCurrency,
        recommendedDataCenter: this.getRecommendedDataCenter()
      };
    } catch (error) {
      console.error('Error initializing geolocation service:', error);
      return {
        location: this.getDefaultLocation(),
        currency: 'INR',
        recommendedDataCenter: 'India'
      };
    }
  }
}

// Create a singleton instance
export const geoLocationService = new GeoLocationService();
